import { useCallback, useMemo } from "react";
import ChartHelpers from "helpers/ChartHelpers";
import type { ScaleLinear } from "types/charts";

import YAxisStoreContext from "./contexts/YAxisStoreContext";
import YAxisActionsContext from "./contexts/YAxisActionsContext";
import useAreaChart from "../AreaChartProvider/useAreaChartStore/useAreaChartStore";
import useAreaChartActions from "../AreaChartProvider/useAreaChartActions";
import { DEFAULT_Y_AXIS_KEY } from "../constants";

interface YAxisProviderProps {
  children?: React.ReactNode;
}

function YAxisProvider(props: YAxisProviderProps) {
  const { series, height, margin, yAxis } = useAreaChart();
  const { yAccessor } = useAreaChartActions();

  const scale = useMemo(() => {
    const allSeriesData = series.reduce((acc, serie) => [...acc, ...serie.data.map(yAccessor)], [] as number[]);
    const [minValue, maxValue] = series.reduce(
      (acc, serie) => {
        return [Math.min(acc[0], serie.minValue || 0), Math.max(acc[1], serie.maxValue || 0)];
      },
      [series[0].minValue || 0, 0]
    );

    return ChartHelpers.constructScaleLinear({
      data: allSeriesData,
      accessor: (d) => d,
      height,
      margin,
      maxValue,
      minValue,
      plotFromZero: false,
      reverse: yAxis[0].reverse,
    });
  }, [series, height, margin, yAxis, yAccessor]);

  const scales = useMemo(() => {
    return yAxis.reduce(
      (acc, item) => {
        if (!item.axisId) {
          return acc;
        }

        const serie = series.find((entry) => entry.yAxisId === item.axisId);
        const data = serie?.data || [];

        const scale = ChartHelpers.constructScaleLinear({
          data: data,
          accessor: yAccessor,
          height,
          margin,
          maxValue: serie?.maxValue,
          minValue: serie?.minValue,
          plotFromZero: serie?.plotFromZero || false,
          reverse: item.reverse,
        });

        return {
          ...acc,
          [item.axisId]: scale,
        };
      },
      {} as Record<string, ScaleLinear>
    );
  }, [yAxis, series, yAccessor, height, margin]);

  const getYScale = useCallback(
    (axisId = DEFAULT_Y_AXIS_KEY): ScaleLinear => {
      return scales[axisId] || scale;
    },
    [scale, scales]
  );

  const actions = useMemo(() => ({ getYScale }), [getYScale]);

  const store = useMemo(() => {
    return {
      yAxis,
      scales,
    };
  }, [scales, yAxis]);

  return (
    <YAxisActionsContext.Provider value={actions}>
      <YAxisStoreContext.Provider value={store}>{props.children}</YAxisStoreContext.Provider>
    </YAxisActionsContext.Provider>
  );
}

export default YAxisProvider;
